import { useEffect, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormTrigger,
  UseFormWatch,
  RegisterOptions,
  FieldPath
} from "react-hook-form";
import { BaseButton } from "components/buttons/BaseButton";
import { useKeyPress } from "hooks/useKeyPress";
import { ISignUpFieldValues } from "../AuthSignUp";
import { TextInput } from "components/inputs/TextInput/TextInput";
import { AuthPopupPasswordCheck } from "../AuthPopupPasswordCheck";
import { TogglePasswordButton } from "components/buttons/TogglePasswordButton";
import authPopupClasses from "./../AuthPopup.module.scss";

interface IProps {
  next: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  watch: UseFormWatch<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

export const MIN_PASS_LENGTH = 8;
export const MAX_PASS_LENGTH = 20;
// https://owasp.org/www-community/OWASP_Validation_Regex_Repository
const passwordRegexp = new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,20}$/);

const SignUpPasswordStep = (props: IProps) => {
  const { next, control, trigger, watch, getValues } = props;

  const [passLengthIsOk, setPassLengthIsOk] = useState(false);
  const [passSymbolsIsOk, setPassSymbolsIsOk] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const enterPressed = useKeyPress("Enter");

  const handleNext = async () => {
    if (await trigger("password") && await trigger("password_confirm")) {
      next();
    }
  };

  const validate = (val: string) => {
    if (!val || val.length < MIN_PASS_LENGTH || val.length > MAX_PASS_LENGTH) {
      setPassLengthIsOk(false);
    } else {
      setPassLengthIsOk(true);
    }

    if (passwordRegexp.test(val)) {
      setPassSymbolsIsOk(true);
    } else {
      setPassSymbolsIsOk(false);
    }
  };

  useEffect(() => {
    validate(getValues().password);
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
    // eslint-disable-next-line
  }, [enterPressed])

  return (
    <div className={authPopupClasses.ContentContainer}>
      <Controller
        name="password"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
          },
          minLength: {
            value: MIN_PASS_LENGTH,
          },
          maxLength: {
            value: MAX_PASS_LENGTH,
          },
          pattern: {
            value: passwordRegexp,
          },
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <div>
            <TextInput
              label="Password"
              type={showPassword ? "text" : "password"}
              onChange={async (e) => {
                field.onChange(e);
                validate(e.target.value);
                await trigger("password");

                const passwordConfirm = watch<FieldPath<ISignUpFieldValues>>("password_confirm");
                if (passwordConfirm) {
                  await trigger("password_confirm");
                }
              }}
              value={field.value}
              error={fieldState.error ? fieldState.error.message : ""}
              rightComponent={
                <TogglePasswordButton onClick={() => setShowPassword(prev => !prev)} isShow={showPassword}/>
              }
            />

            <AuthPopupPasswordCheck
              minLength={MIN_PASS_LENGTH}
              maxLength={MAX_PASS_LENGTH}
              passLengthIsOk={passLengthIsOk}
              passSymbolsIsOk={passSymbolsIsOk}
            />
          </div>
        )}
      />

      <Controller
        name="password_confirm"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Required Field!"
          },
          validate: {
            value: (value) => {
              return value === watch<FieldPath<ISignUpFieldValues>>("password") || "Passwords do not match!";
            }
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <TextInput
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            onChange={async (e) => {
              field.onChange(e);
              await trigger("password_confirm");
            }}
            value={field.value}
            error={fieldState.error ? fieldState.error.message : ""}
            rightComponent={
              <TogglePasswordButton onClick={() => setShowConfirmPassword(prev => !prev)} isShow={showConfirmPassword}/>
            }
          />
        )}
      />

      <div className={authPopupClasses.Footer}>
        <BaseButton type="button" onClick={handleNext}>Next</BaseButton>
      </div>
    </div>
  );
};

export default SignUpPasswordStep;
